import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { fmt, fmtQty, unitLabel } from '../utils/format';
import Alert from '../components/Alert';
import Pager from '../components/Pager';
import ProductModal from '../components/ProductModal';
import ConfirmModal from '../components/ConfirmModal';
import CategoryFilter from '../components/CategoryFilter';

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [category, setCategory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState(null);
  const [modalProduct, setModalProduct] = useState(undefined);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Debounce search input → reset to page 1
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Fetch whenever applied filters or page change
  useEffect(() => {
    setLoading(true);
    const params = { page };
    if (appliedSearch.trim()) params.q = appliedSearch.trim();
    if (category.length) params.category = category.join(',');
    api.get('/products', { params })
      .then(res => {
        setProducts(res.data.products);
        setTotal(res.data.total || 0);
        setPages(res.data.pages || 1);
        if (res.data.categories) setCategories(res.data.categories);
      })
      .catch(() => setError('Could not load products.'))
      .finally(() => setLoading(false));
  }, [appliedSearch, category, page]);

  function handleCategoryChange(newCats) {
    setCategory(newCats);
    setPage(1);
  }

  function clearFilters() {
    setSearchInput('');
    setAppliedSearch('');
    setCategory([]);
    setPage(1);
  }

  async function handleSave(form) {
    try {
      if (modalProduct) {
        const res = await api.put(`/products/${modalProduct.id}`, form);
        setProducts(prev => prev.map(p => p.id === res.data.product.id ? res.data.product : p));
      } else {
        const res = await api.post('/products', form);
        setProducts(prev => [res.data.product, ...prev]);
        setTotal(t => t + 1);
      }
      setModalProduct(undefined);
      setAlert({ type: 'success', msg: 'Product saved.' });
    } catch (err) {
      throw err;
    }
  }

  async function confirmDelete() {
    try {
      await api.delete(`/products/${deleteTarget.id}`);
      setProducts(prev => prev.filter(p => p.id !== deleteTarget.id));
      setTotal(t => t - 1);
      setAlert({ type: 'success', msg: 'Product deleted.' });
    } catch (err) {
      setAlert({ type: 'danger', msg: err?.response?.data?.error || 'Could not delete product.' });
    } finally {
      setDeleteTarget(null);
    }
  }

  const hasFilters = searchInput || category.length > 0;

  return (
    <Layout title="Product Management">
      <Alert type={alert?.type} message={alert?.msg} onClose={() => setAlert(null)} />
      <Alert type="danger" message={error} onClose={() => setError('')} />

      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ maxWidth: 240 }}
          placeholder="Search by name, SKU, barcode…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />
        <CategoryFilter categories={categories} selected={category} onChange={handleCategoryChange} />
        {hasFilters && (
          <button className="btn" onClick={clearFilters}>
            <i className="ti ti-x" aria-hidden="true"></i>Clear
          </button>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn btn-primary" onClick={() => setModalProduct(null)}>
            <i className="ti ti-plus" aria-hidden="true"></i>Add product
          </button>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="empty-state">Loading products…</div>
        ) : products.length === 0 ? (
          <div className="empty-state">No products found</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>SKU</th>
                <th>Barcode</th>
                <th>Category</th>
                <th>MRP</th>
                <th>Price</th>
                <th>Base Pack / Unit</th>
                <th>Qty</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, i) => (
                <tr key={p.id}>
                  <td>{(page - 1) * 20 + i + 1}</td>
                  <td style={{ maxWidth: 200, fontWeight: 600, fontSize: 11 }}>{p.name}</td>
                  <td>{p.sku}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{p.barcode}</td>
                  <td>{p.category}</td>
                  <td>{fmt(p.mrp)}</td>
                  <td>{fmt(p.price)}</td>
                  <td>
                    {p.is_loose
                      ? <span style={{ fontWeight: 600, color: 'var(--primary, #1976d2)' }}>{unitLabel(p.unit)} (loose)</span>
                      : p.base_pack}
                  </td>
                  <td>{fmtQty(p.qty, p.unit)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm btn-primary" onClick={() => setModalProduct(p)}>
                        <i className="ti ti-edit" aria-hidden="true"></i>
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => setDeleteTarget(p)}>
                        <i className="ti ti-trash" aria-hidden="true"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pager page={page} pages={pages} total={total} onPage={setPage} label="products" />

      {modalProduct !== undefined && (
        <ProductModal product={modalProduct} onCancel={() => setModalProduct(undefined)} onSave={handleSave} />
      )}
      {deleteTarget && (
        <ConfirmModal
          title="Delete product"
          message={<>Delete "<strong>{deleteTarget.name}</strong>"?</>}
          confirmLabel="Delete"
          danger
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </Layout>
  );
}
